import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteCard } from '@metro/route-card'
import { buildRouteCardCopy } from '../i18n/packageStrings'
import { MobileRouteDetailsOverlay } from './MobileRouteDetailsOverlay'
import { useBrowserBackDismiss } from './useBrowserBackDismiss'
import { Suggester } from '@metro/suggester'
import { useGeolocation } from '../state/geolocationContext'
import { useRouteSelection } from '../state/useRouteSelection'
import { useMetroMapDispatch, useMetroMapStore } from '../state/useMetroMapStore'
import { effectiveTheme } from '../state/effectiveTheme'
import { useRoutingAdapter } from '../state/useRoutingAdapter'
import { routeTimesClock } from '../state/routeTimesClock'
import { useSuggesterPanel } from '../state/useSuggesterPanel'
import { useRouteDetailsPayload } from '../state/useRouteDetailsPayload'
import { useFeedback } from '../state/useFeedback'
import { useRouteHistoryRecorder } from '../state/routeLibrary/useRouteHistoryRecorder'
import { useRouteVariantTappedReporter } from '../state/useRouteVariantTappedReporter'
import { useRouteCardEdits } from '../state/useRouteCardEdits'
import { useSchema } from './useSchema'
import { lineHexColorFromId, resolveStationById } from '../interaction/stationResolver'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'
import { BREAKPOINT_PX } from './StationCardOrDetailsRouter'
import styles from './MobileRouteCardOverlay.module.css'

export interface MobileRouteCardOverlayProps {
  /**
   * Currently-selected station snapshot from the store. The overlay hides
   * when this is non-null because the StationCard carousel takes the
   * same bottom-of-viewport slot — RouteCard and StationCard are
   * mutually exclusive on the phone surface.
   */
  readonly selectedStation: SelectedStationSnapshot | null
}

/**
 * Mobile-only floating bottom overlay that renders the RouteCard
 * `phone` variant when the viewport is narrower than 768px and no
 * station is currently selected.
 *
 * Hide-conditions are evaluated in fixed order (viewport → selection)
 * so the early-exit path is easy to follow when reviewing
 * surface-routing logic. The desktop counterpart is mounted by
 * {@link StationCardOrDetailsRouter} as part of its expanded-sidebar
 * branch — this component MUST NOT render at >= 768px.
 *
 * matchMedia subscription is via `useSyncExternalStore` — idiomatic
 * React-19 reactive viewport reads with no extra `useEffect`.
 *
 * TASK-318: tapping a From/To pill writes `?suggester=from|to` to the
 * URL, which mounts the full-screen {@link Suggester} overlay.
 * Selecting a station dispatches `RouteFromSelected` / `RouteToSelected`
 * and drops the query param to dismiss the Suggester.
 */
export function MobileRouteCardOverlay({
  selectedStation,
}: MobileRouteCardOverlayProps): ReactElement | null {
  const geolocation = useGeolocation()
  const { t } = useTranslation('route-card')
  const routeCardCopy = useMemo(() => buildRouteCardCopy(t), [t])
  const routeSelection = useRouteSelection()
  const dispatch = useMetroMapDispatch()
  const schema = useSchema()
  const adapter = useRoutingAdapter()
  const reportVariantTapped = useRouteVariantTappedReporter()
  const routeCardEdits = useRouteCardEdits()
  const store = useMetroMapStore()
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)
  const selectedLine = useSyncExternalStore(store.subscribe, () => store.getState().selectedLine)
  // The EFFECTIVE theme — the signal that picks the map's light/dark
  // MMTG geometry, night-tube included (2026-07 color contract).
  const theme = useSyncExternalStore(store.subscribe, () => effectiveTheme(store.getState()))
  // I (Feedback): RouteDetails's ReportIssueRow opens the Feedback form
  // with the `routeDetails` context (mirrors iOS).
  const { openFeedback } = useFeedback()

  // Suggester picker wiring — shared with the desktop side panel
  // (`StationCardOrDetailsRouter`) so the two surfaces can never drift
  // (spec §5). Reads the `?suggester=` slot param and owns the
  // open/close/select plumbing.
  const suggester = useSuggesterPanel()

  // RouteDetails payload (rawRoutes / selectedRoute / lineLookup /
  // resolveStation / possibleTransfersByLeg / canOpen) — shared with the
  // desktop side panel so both surfaces build the identical inputs (spec
  // §5). Live S4/S5 convenient cars come from the bundled schema inside.
  const details = useRouteDetailsPayload()

  // RouteDetails modal open flag. Opened by the RouteCard "Route
  // Details" button (results state), dismissed by backdrop / Escape.
  const [detailsOpen, setDetailsOpen] = useState(false)

  const isExpanded = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
    () => false, // SSR fallback — unused in CSR-only metro-frontend.
  )

  // The RouteDetails modal is visible only while the user has opened it
  // AND the route lifecycle is still in `results` — derived, not an
  // effect, so leaving results (e.g. the user clears a slot) hides the
  // modal on the next render without a setState-in-effect cascade. The
  // `detailsOpen` flag itself only flips via the explicit open / close
  // handlers below.
  const detailsVisible = detailsOpen && routeStatus.kind === 'results'

  // Bug 5: the RouteDetails modal is state-only (no URL entry of its own),
  // so the browser Back button used to pop the last real history entry and
  // strand the user on a prerendered SEO shell. While the modal is visible we
  // push a synthetic same-URL history entry; Back pops it and closes the
  // overlay (staying in the SPA), matching the close button / backdrop tap.
  const closeDetails = useCallback(() => setDetailsOpen(false), [])
  // `route_card_open_route_details_tapped` — the iOS name and keys
  // (`MetroMapMetalRootView+RouteCard.swift`). Phone only: the desktop side
  // panel has no Route Details button, it shows the details inline.
  const openDetails = useCallback(() => {
    setDetailsOpen(true)
    realMetroAnalyticsReporter('route_card_open_route_details_tapped', {
      station_from_id: routeSelection.fromStation?.id ?? '',
      station_to_id: routeSelection.toStation?.id ?? '',
    })
  }, [routeSelection])
  useBrowserBackDismiss(detailsVisible, closeDetails)

  // TASK-733: the phone half of the single route-library write point. The
  // rider opening RouteDetails is the one moment history is recorded — the
  // desktop side panel (`StationCardOrDetailsRouter`) calls the SAME hook
  // with its own open condition, so the two surfaces cannot drift.
  //
  // `SelectedStationSnapshot` carries an `id`, not a `slug`; the route key
  // is keyed on the rename-stable cluster slug, so both ends are resolved
  // through the schema exactly like `useRouteDetailsPayload` resolves names.
  //
  // `isOpen` repeats this component's own early-exit conditions rather than
  // reusing `detailsVisible` alone: the flag survives a resize past the
  // breakpoint, and without the viewport / takeover clauses a phone→desktop
  // resize would record from a modal that is no longer on screen — while the
  // side panel, correctly, records the same route too.
  useRouteHistoryRecorder({
    isOpen: detailsVisible && !isExpanded && selectedStation === null && suggester.slot === null,
    fromSlug:
      resolveStationById(schema, routeSelection.fromStation?.id ?? '')?.station.slug ?? null,
    toSlug: resolveStationById(schema, routeSelection.toStation?.id ?? '')?.station.slug ?? null,
    route: details.selectedRoute,
    reporter: realMetroAnalyticsReporter,
  })

  // Escape closes the route card when the lifecycle is active (i.e. the
  // close-button is visible). Mirrors StationDetailsModal escape behaviour.
  // Audit ref: H-13.
  const routeLifecycleActive = routeStatus.kind !== 'collapsed'
  useEffect(() => {
    if (!routeLifecycleActive) return
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      // When the RouteDetails modal is open, Escape belongs to the
      // modal (it dismisses itself) — don't also clear the route card.
      if (detailsVisible) return
      dispatch({ kind: 'RouteToCleared' })
      dispatch({ kind: 'RouteCleared' })
    }
    window.addEventListener('keydown', onKeyDown)
    return (): void => window.removeEventListener('keydown', onKeyDown)
  }, [routeLifecycleActive, dispatch, detailsVisible])

  // Fixed evaluation order: viewport → carousel takeover → line card. Each returns
  // null individually rather than via a boolean OR so the failure cause
  // is visible in stack traces and easy to reorder.
  if (isExpanded) return null
  if (selectedStation !== null) return null
  // The line view's card owns the bottom slot (`LineCardOverlay`) until a
  // route takes over or the picker opens.
  if (selectedLine !== null && routeStatus.kind === 'collapsed' && suggester.slot === null) {
    return null
  }

  // Suggester takes the whole screen — when its query param is set it
  // replaces the RouteCard overlay entirely.
  // `layout="overlay"` (the default) is the phone full-screen modal.
  //
  // TASK-412 U+V — `key` on the slot so EVERY present is a fresh instance.
  // Suggester owns its store + the mount-time permission probe in a
  // `useRef` / one-shot effect; without a slot-keyed remount a re-present
  // (close→reopen, or from↔to switch) would reuse the latched instance and
  // never re-run the probe, leaving the location banner / Nearby auto-
  // resolve stuck at the first present's state. Keying on the slot mirrors
  // iOS keying the `.task` on the store identity (re-present re-runs mount).
  if (suggester.slot !== null) {
    return <Suggester key={suggester.slot} layout="overlay" {...suggester.props(suggester.slot)} />
  }

  // Resolve hex colors from schema; the snapshot does NOT carry color
  // (shape-stable identifier, not render payload) — same pattern as the
  // desktop branch in StationCardOrDetailsRouter. Schema may be null on
  // a fresh load before the SchemaProvider promise resolves; treat that
  // as "no resolution" (button stays in placeholder state).
  const fromLineColor =
    routeSelection.fromStation === null || schema === null
      ? null
      : lineHexColorFromId(schema, routeSelection.fromStation.lineId, theme)
  const toLineColor =
    routeSelection.toStation === null || schema === null
      ? null
      : lineHexColorFromId(schema, routeSelection.toStation.lineId, theme)

  return (
    <div data-testid="route-card-overlay" className={styles.overlay}>
      <div className={styles.carouselHost}>
        {/* Collapsed-only 8px gutter (enlarged low-vision pills sit closer
            to the screen edge); the active route lifecycle keeps the base
            16px gutter so the results panel is unchanged. Mirrors iOS
            `_routeStatus.kind == .collapsed ? 8 : 16` and Android. */}
        <div
          className={
            routeStatus.kind === 'collapsed'
              ? `${styles.carousel} ${styles.carouselCollapsed}`
              : styles.carousel
          }
        >
          <div className={styles.slot}>
            <RouteCard
              variant="phone"
              copy={routeCardCopy}
              from={
                routeSelection.fromStation === null || fromLineColor === null
                  ? null
                  : {
                      id: routeSelection.fromStation.id,
                      shortLocalName: routeSelection.fromStation.localName,
                      lineHexColor: fromLineColor,
                    }
              }
              to={
                routeSelection.toStation === null || toLineColor === null
                  ? null
                  : {
                      id: routeSelection.toStation.id,
                      shortLocalName: routeSelection.toStation.localName,
                      lineHexColor: toLineColor,
                    }
              }
              onFromTap={() => suggester.open('from')}
              onToTap={() => suggester.open('to')}
              onFromClear={routeCardEdits.onFromClear}
              onToClear={routeCardEdits.onToClear}
              onSwap={routeCardEdits.onSwap}
              onMyLocationTap={geolocation?.onMyLocation}
              isResolvingMyLocation={geolocation?.isResolvingMyLocation ?? false}
              routeStatus={routeStatus}
              lineLookup={adapter?.lineLookupFor(theme)}
              onSelectVariant={(idx) => {
                dispatch({ kind: 'RouteSelectedVariantChanged', index: idx })
                reportVariantTapped(idx)
              }}
              // Opens the RouteDetails modal dialog with the selected
              // route. Wired only in the results lifecycle (and once the
              // routing adapter / schema have loaded); otherwise the
              // button stays inert.
              onOpenRouteDetails={details.canOpen ? openDetails : undefined}
              clock={routeTimesClock(routeStatus)}
              onClose={() => {
                // Close button keeps the From slot — user can pick a new To
                // without re-entering the origin. Only To and the routeStatus
                // lifecycle reset.
                dispatch({ kind: 'RouteToCleared' })
                dispatch({ kind: 'RouteCleared' })
              }}
            />
          </div>
        </div>
      </div>

      {detailsVisible && details.selectedRoute !== null && details.lineLookup !== null && (
        <MobileRouteDetailsOverlay
          rawRoutes={details.rawRoutes}
          selectedRoute={details.selectedRoute}
          // Web is 100% free (no paywall) — premium content for everyone,
          // so the S5 upsell banner never renders and the S4 car-strip shows.
          isPremium={true}
          lineLookup={details.lineLookup}
          resolveStation={details.resolveStation}
          possibleTransfersByLeg={details.possibleTransfersByLeg}
          platformBoundsByLeg={details.platformBoundsByLeg}
          // Ride-leg indices that render the striped connector (DLR /
          // Overground) — matches the map's dashed lines.
          outlinedLegs={details.outlinedLegs}
          onClose={closeDetails}
          onSelectRoute={(route) => {
            const idx = details.rawRoutes.findIndex((r) => r.id === route.id)
            if (idx >= 0) dispatch({ kind: 'RouteSelectedVariantChanged', index: idx })
          }}
          onOpenTransferPaywall={() => {
            // Web has no paywall surface yet — opening the transfer
            // paywall is a no-op until the web Adapty integration lands
            // (placement `com.mmthstudio.possible-transfer.v1`). The
            // analytics event still fires inside the overlay.
          }}
          onReportIssue={() => openFeedback('routeDetails')}
          clock={routeTimesClock(routeStatus)}
        />
      )}
    </div>
  )
}
